import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import SEOMetadata from '../components/SEOMetadata';
import { getTheme } from '../utils/theme';

const theme = getTheme();

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname.replace('/', '');
  
  // High-performance SEO Keywords & Content Mapping
  const contentMap = {
    'random-chat': {
      title: 'Random Chat — The Ultimate Platform to Meet Strangers Online',
      h1: 'Premier Random Chat Experience',
      desc: 'Join the world\'s most popular random chat. Connect instantly with interesting strangers via text or video. 100% free and private.',
      keywords: 'random chat, chat with strangers, meet people online, random video chat, anonymous messaging'
    },
    'stranger-chat': {
      title: 'Stranger Chat — Safe & Anonymous Talk with Random People',
      h1: 'Anonymous Stranger Chat',
      desc: 'Talk to strangers in a safe, secure, and anonymous environment. No login required. Start a conversation with a random person now.',
      keywords: 'stranger chat, talk to strangers, anonymous talk, random people, private chat'
    },
    'online-chat': {
      title: 'Online Chat — Free Instant Messaging & Global Connections',
      h1: 'Best-in-Class Online Chat',
      desc: 'Experience seamless online chatting with users from all over the world. Our platform offers the fastest way to start a real-time conversation.',
      keywords: 'online chat, free chat rooms, instant messaging, global chat, live messaging'
    },
    'anonymous-chat': {
      title: 'Anonymous Chat — Chat Privately Without Identity or Registration',
      h1: 'Secure Anonymous Chatting',
      desc: 'Keep your identity private while meeting new people. Our anonymous chat platform requires zero personal information.',
      keywords: 'anonymous chat, no registration chat, private messaging, hidden identity chat'
    },
    'video-chat': {
      title: 'Random Video Chat — High-Quality Face-to-Face Interaction',
      h1: 'Crystal Clear Video Chat',
      desc: 'Engage in high-definition random video calls with strangers. Meet real people face-to-face from the comfort of your home.',
      keywords: 'video chat, random cam chat, live video talk, face-to-face chat'
    },
    'live-random-chat': {
      title: 'Live Random Chat — Real-Time Conversations with Verified Users',
      h1: 'Genuine Live Random Chat',
      desc: 'Tired of bots? Join our live random chat community and connect with real, active users looking for genuine conversation.',
      keywords: 'live chat, real people chat, active chat rooms, verified random chat'
    },
    'global-chat': {
      title: 'Global Chat — Connect with the World in One Click',
      h1: 'Worldwide Global Connection',
      desc: 'Break borders with our global chat platform. Meet people from different cultures, languages, and countries instantly.',
      keywords: 'global chat, international chat, world chat rooms, cross-border chat'
    },
    'roblox-chat': {
      title: 'Roblox Chat — The #1 Unofficial Chat for Roblox Fans',
      h1: 'Roblox Community Hub',
      desc: 'The ultimate destination for Roblox players. Hang out, discuss game updates, and trade tips with other Roblox fans.',
      keywords: 'roblox chat, roblox hangout, roblox community, roblox fans, gaming chat'
    },
    'roblox-community-hangout': {
      title: 'Roblox Community Hangout — Best Place for Fans to Talk',
      h1: 'Official-Style Roblox Hangout',
      desc: 'A dedicated space for the Roblox community. Meet your next gaming partner or share your latest creations in our hangout.',
      keywords: 'roblox hangout, roblox friends, roblox fan club, roblox talk'
    },
    'roblox-gamers-chat': {
      title: 'Roblox Gamers Chat — Real-Time Strategy & Gaming Talk',
      h1: 'Pro Roblox Gaming Chat',
      desc: 'Join the conversation with serious Roblox gamers. Perfect for discussing strategies, new releases, and game mechanics.',
      keywords: 'roblox gamers, roblox game chat, pro gaming chat, roblox strategy'
    },
    'anonymous-messaging': {
      title: 'Anonymous Messaging — Send Private Messages Instantly',
      h1: '100% Anonymous Messaging',
      desc: 'Send and receive messages without revealing your identity. The fastest and most secure way to chat anonymously.',
      keywords: 'anonymous messaging, private texting, untraceable chat'
    },
    'stranger-talk': {
      title: 'Stranger Talk — Voice & Text Chat with Strangers',
      h1: 'Real-Time Stranger Talk',
      desc: 'Talk to strangers across the globe. Join the conversation and share your thoughts with people you\'ve never met.',
      keywords: 'stranger talk, talk with strangers, voice stranger chat'
    },
    'random-people-chat': {
      title: 'Random People Chat — Meet & Greet Random Strangers',
      h1: 'Meet Random People Instantly',
      desc: 'Connect with a diverse set of random people. Perfect for expanding your social circle and learning new perspectives.',
      keywords: 'random people chat, meet randoms, social chat rooms'
    },
    'privacy-focused-chat': {
      title: 'Privacy-Focused Chat — Your Security is Our Priority',
      h1: 'The Most Secure Chat Online',
      desc: 'A chat platform designed for privacy advocates. No logs, no tracking, just pure anonymous conversation.',
      keywords: 'privacy chat, secure messaging, encrypted random chat'
    },
    'friend-chat': {
      title: 'Friend Chat — Chat with Friends and Meet New People',
      h1: 'Social Friend Chat Rooms',
      desc: 'Connect with your friends or meet new people who share your interests. The best place for social chatting.',
      keywords: 'friend chat, social chat, meet friends online'
    },
    // Default fallback
    'default': {
      title: 'LLOOP — Best Free Random Chat with Strangers',
      h1: 'Meet Someone Special Today',
      desc: 'The most advanced random chat platform. Connect instantly with strangers worldwide through text, voice, and video.',
      keywords: 'random chat, lloop chat, meet strangers, free chat online'
    }
  };

  const content = contentMap[path] || contentMap['default'];

  return (
    <div style={S.container}>
      <div style={{ marginBottom: 40 }}>
        {theme.brandLogo.startsWith('/') ? <img src={theme.brandLogo} alt={theme.brandName} style={{ height: 50 }} /> : <h2 style={S.logoText}>{theme.brandLogo}</h2>}
      </div>

      <SEOMetadata 
        title={content.title}
        description={content.desc}
        keywords={content.keywords}
      />
      
      <div style={S.hero}>
        <div style={S.badge}>✨ Trusted by Millions Worldwide</div>
        <h1 style={S.title}>{content.h1}</h1>
        <p style={S.subtitle}>{content.desc}</p>
        
        <div style={S.btnGroup}>
          <button style={S.cta} onClick={() => navigate('/random')}>
            Start Chatting Now
          </button>
          <button style={S.ctaSecondary} onClick={() => navigate('/register')}>
            Create Account (Free)
          </button>
        </div>
        
        <p style={S.noReg}>⚡ No installation or registration required to start.</p>
      </div>

      <div style={S.statsRow}>
        <div style={S.statItem}>
          <div style={S.statVal}>50k+</div>
          <div style={S.statLab}>Online Now</div>
        </div>
        <div style={S.statItem}>
          <div style={S.statVal}>10M+</div>
          <div style={S.statLab}>Matches Daily</div>
        </div>
        <div style={S.statItem}>
          <div style={S.statVal}>190+</div>
          <div style={S.statLab}>Countries</div>
        </div>
      </div>

      <div style={S.seoContent}>
        <h2 style={S.subTitle}>Experience the Future of Online Chatting</h2>
        <p style={S.text}>
          Our platform is engineered to provide the best {path.replace('-', ' ')} experience available today. 
          By utilizing state-of-the-art WebRTC technology, we offer low-latency video and instant text delivery 
          that makes you feel like you're in the same room as your chat partner.
        </p>
        
        <div style={S.featureGrid}>
          <div style={S.featCard}>
            <div style={S.featIcon}>🛡️</div>
            <div style={S.featTitle}>Privacy First</div>
            <div style={S.featDesc}>End-to-end encryption for your peace of mind. Your data never leaves the chat.</div>
          </div>
          <div style={S.featCard}>
            <div style={S.featIcon}>🚀</div>
            <div style={S.featTitle}>Instant Match</div>
            <div style={S.featDesc}>Our smart algorithm pairs you with the most compatible stranger in under 0.5 seconds.</div>
          </div>
          <div style={S.featCard}>
            <div style={S.featIcon}>🌈</div>
            <div style={S.featTitle}>No Bots</div>
            <div style={S.featDesc}>Strict AI-powered moderation ensures you only talk to real, genuine people.</div>
          </div>
          <div style={S.featCard}>
            <div style={S.featIcon}>📱</div>
            <div style={S.featTitle}>Mobile Ready</div>
            <div style={S.featDesc}>Works perfectly on any browser, whether you're on iPhone, Android, or Desktop.</div>
          </div>
        </div>

        <h3 style={S.subTitle}>How to Start Using Our {content.h1}</h3>
        <p style={S.text}>
          Starting your journey is simple. Click the 'Start Chatting Now' button above. 
          You'll be instantly connected to a random person. If you like the conversation, 
          you can add them as a friend. If not, just click 'Next' to meet someone new. 
          It's that easy to enjoy {path.replace('-', ' ')}!
        </p>
      </div>

      <footer style={S.footer}>
        <p>&copy; 2026 LLOOP Chat Platform. All rights reserved.</p>
        <div style={S.footLinks}>
          <span onClick={() => navigate('/feedback')}>Feedback</span>
          <span onClick={() => navigate('/login')}>Login</span>
          <span onClick={() => navigate('/register')}>Sign Up</span>
        </div>
      </footer>
    </div>
  );
};

const S = {
  container: {
    maxWidth: 1000,
    margin: '0 auto',
    padding: '40px 20px',
    textAlign: 'center',
    color: '#e2e8f0',
    fontFamily: "'Inter', sans-serif",
    height: '100%',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch'
  },
  badge: {
    background: 'rgba(99,102,241,0.15)',
    color: theme.colors.primary,
    padding: '6px 16px',
    borderRadius: 30,
    fontSize: 12,
    fontWeight: 700,
    display: 'inline-block',
    marginBottom: 20,
    border: `1px solid rgba(99,102,241,0.3)`
  },
  hero: {
    marginBottom: 40,
    display: 'flex',

    flexDirection: 'column',
    alignItems: 'center'
  },
  title: {
    fontSize: 'clamp(32px, 5vw, 60px)',
    fontWeight: 900,
    marginBottom: 20,
    background: theme.colors.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-2px',
    lineHeight: 1.1
  },
  subtitle: {
    fontSize: 'clamp(16px, 2vw, 20px)',
    color: '#94a3b8',
    marginBottom: 40,
    lineHeight: 1.6,
    maxWidth: 700
  },
  btnGroup: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: 24
  },
  cta: {
    padding: '18px 40px',
    borderRadius: 16,
    border: 'none',
    background: theme.colors.gradient,
    color: '#fff',
    fontSize: 18,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: `0 10px 40px rgba(99,102,241,0.4)`,
    transition: 'all 0.2s ease'
  },
  ctaSecondary: {
    padding: '18px 40px',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.03)',
    color: '#f1f5f9',
    fontSize: 18,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  noReg: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: 500
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: 20,
    marginBottom: 60,
    padding: '20px 0',

    borderTop: '1px solid rgba(255,255,255,0.05)',
    borderBottom: '1px solid rgba(255,255,255,0.05)'
  },
  statItem: { 
    display: 'flex', 
    flexDirection: 'column', 
    gap: 12, 
    alignItems: 'center',
    padding: '20px'
  },
  statVal: { 
    fontSize: 'clamp(28px, 5vw, 36px)', 
    fontWeight: 900, 
    background: theme.colors.gradient, 
    WebkitBackgroundClip: 'text', 
    WebkitTextFillColor: 'transparent',
    lineHeight: 1.2
  },
  statLab: { 
    fontSize: 12, 
    color: '#cbd5e1', 
    textTransform: 'uppercase', 
    fontWeight: 800, 
    letterSpacing: '1px' 
  },


  seoContent: {
    textAlign: 'left',
    background: '#111118',
    padding: '60px 40px',
    borderRadius: 32,
    border: '1px solid rgba(255,255,255,0.05)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
  },
  subTitle: {
    fontSize: 28,
    fontWeight: 800,
    marginBottom: 20,
    color: '#f1f5f9',
    letterSpacing: '-0.5px'
  },
  text: {
    fontSize: 16,
    color: '#94a3b8',
    lineHeight: 1.8,
    marginBottom: 40
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 24,
    marginBottom: 60
  },
  featCard: {
    background: 'rgba(255,255,255,0.02)',
    padding: 30,
    borderRadius: 24,
    border: '1px solid rgba(255,255,255,0.05)',
    transition: 'transform 0.2s ease'
  },
  featIcon: { fontSize: 32, marginBottom: 16 },
  featTitle: { fontSize: 18, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 },
  featDesc: { fontSize: 14, color: '#64748b', lineHeight: 1.6 },
  footer: {
    marginTop: 80,
    paddingTop: 40,
    borderTop: '1px solid rgba(255,255,255,0.05)',
    color: '#475569',
    fontSize: 13
  },
  footLinks: {
    display: 'flex',
    gap: 24,
    justifyContent: 'center',
    marginTop: 16,
    fontWeight: 600
  }
};

export default LandingPage;
